import { styled } from '@mui/material';
import SmartGroupFormation from 'components/SmartGroupFormation';
import { GroupCard, TeamSetup } from 'components/Teams';
import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import { PageContext } from 'pages/Teams/PageProvider';
import { useContext, useRef } from 'react';
import { Button, Dropdown, Icon, IconButton, Loading, Stack, Text } from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const TeamsTab = () => {
    const { course, groups, studentAccountUsers } = useApp();
    const { device } = useViewport();
    const { sendOn } = useWebsocket();

    const {
        isWSLoading: isTeamUpdateInProgress,
        setIsWSLoading,
        setDialog,
        isLoading,
        setEditTeams,
        isGroupFormationOpen,
        downloadTeamCSV,
        uploadTeamCSV,
    } = useContext(PageContext);

    const inputRef = useRef(null);

    const unassignedStudents = studentAccountUsers.filter(({ group_id }) => !group_id);

    const handleSelect = ({ key }) => {
        switch (key) {
            case 'import-csv':
                setDialog((current) => ({
                    ...current,
                    open: true,
                    message: 'Import New CSV?',
                    description: 'This will override all existing team data and erase their progress permanently.',
                    variant: 'destructive',
                    actionButtonText: 'Import',
                    onAction: () => inputRef.current.click(),
                }));
                break;
            case 'delete-all-teams':
                setDialog((current) => ({
                    ...current,
                    open: true,
                    message: 'Delete All Teams?',
                    description: 'This will delete all existing team data and erase their progress permanently.',
                    variant: 'destructive',
                    actionButtonText: 'Delete',
                    onAction: () => {
                        const teamChanges = groups.map(({ id }) => ({
                            type: 'group',
                            action: 'delete',
                            payload: { id },
                        }));
                        const studentChanges = studentAccountUsers
                            .filter(({ group_id }) => group_id)
                            .map((accountUser) => ({
                                type: 'member',
                                action: 'move',
                                payload: {
                                    id: accountUser.user_id,
                                    from: accountUser.group_id,
                                    to: '',
                                },
                            }));
                        setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                        setIsWSLoading(true);
                        sendOn('default', {
                            payload_type: 'update_user_teams',
                            payload: {
                                course_id: course.id,
                                team_changes: teamChanges,
                                student_changes: studentChanges,
                            },
                        });
                    },
                }));
                break;
            default:
                break;
        }
    };

    if (isLoading || isTeamUpdateInProgress) return <Loading />;

    if (isGroupFormationOpen) return <SmartGroupFormation />;

    if (!groups.length) {
        return <TeamSetup />;
    }

    return (
        <CustomStack spacing={2}>
            <Stack
                alignItems={device === 'mobile' ? 'flex-end' : 'center'}
                direction={device === 'mobile' ? 'column' : 'row'}
                justifyContent={'space-between'}
                spacing={device === 'mobile' ? 2 : 0}
            >
                <Text
                    sx={{ ...(device === 'mobile' && { alignSelf: 'flex-start' }) }}
                    component={'h1'}
                    variant={'h6'}
                    weight={'semibold'}
                >
                    Teams
                </Text>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Button startIcon={<Icon kind={'download'} />} variant={'tertiary'} onClick={downloadTeamCSV}>
                        Export Team CSV
                    </Button>
                    <Button
                        startIcon={<Icon kind={'edit'} />}
                        onClick={() =>
                            setEditTeams({
                                open: true,
                                type: '',
                                groupSize: '',
                                numGroups: '',
                                groups,
                                studentAccountUsers,
                                onSubmit: ({ teamChanges, studentChanges }) => {
                                    setIsWSLoading(true);
                                    sendOn('default', {
                                        payload_type: 'update_user_teams',
                                        payload: {
                                            course_id: course.id,
                                            team_changes: teamChanges,
                                            student_changes: studentChanges,
                                        },
                                    });
                                },
                            })
                        }
                    >
                        Edit Teams
                    </Button>
                    <Dropdown
                        id={'teams-dropdown'}
                        options={[
                            { key: 'import-csv', value: 'Import CSV' },
                            { key: 'delete-all-teams', value: 'Delete All Teams' },
                        ]}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                kind={'moreHorizontal'}
                                size={'medium'}
                                variant={'square'}
                            />
                        }
                        onSelect={handleSelect}
                    />
                </Stack>
            </Stack>
            {unassignedStudents.length ? (
                <Stack spacing={2}>
                    <Text variant={'h6'} weight={'semibold'}>
                        Unassigned Students ({unassignedStudents.length})
                    </Text>
                    <Stack
                        sx={{ flexWrap: 'wrap' }}
                        direction={'row'}
                        divider={
                            <Text color={'disabled'} variant={'p2'}>
                                /
                            </Text>
                        }
                        spacing={1}
                    >
                        {unassignedStudents.map((student) => (
                            <Text key={student.owner_account_id} color={'light'} variant={'p2'}>
                                {getUserPreferredFullName(student)}
                            </Text>
                        ))}
                    </Stack>
                </Stack>
            ) : null}
            <Stack sx={{ my: 6 }} spacing={5}>
                {groups.map((group) => (
                    <GroupCard
                        key={group.id}
                        group={group}
                        members={studentAccountUsers.filter((student) => student.group_id === group.id)}
                    />
                ))}
            </Stack>
            <input
                ref={inputRef}
                style={{ display: 'none' }}
                accept={'.csv'}
                type={'file'}
                onClick={(e) => (e.target.value = null)}
                onChange={uploadTeamCSV}
            />
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        paddingTop: theme.spacing(4),
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
    };
});

export default TeamsTab;
